import React, { FC, useCallback, useMemo } from "react";

import { faAngleDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import RoundButton from "@app/component/RoundButton";
import CommonFieldName from "@app/util/fieldModel/CommonFieldName";
import { isSimpleCondition } from "@app/util/viewModel/assert";

import getRangeByDuration from "../utils/getRangeByDuration";

const DAYS = [7, 14, 30, 90, 365];

const messages = defineMessages({
  clear: {
    id: "filters.modifiedInPast.clear",
    defaultMessage: "Clear filter",
    description: "Clear Filter button",
  },
  menuItemLabel: {
    id: "filters.modifiedInPast.menuItemLabel",
    defaultMessage: "{value} {value, plural, one {day} other {days}}",
    description: "No recent contact option label in No Recent Contact filter in Quick filters",
  },
  modifiedInPast: {
    id: "filters.modifiedInPast.label",
    defaultMessage:
      "Modified in the past{count, select, undefined {} other {: {count} {count, plural, one {day} other {days}}}}",
    description: "Modified in past filter",
  },
});

const CLEAR_KEY = -1;

export interface ModifiedInPastFilterProps {
  fieldName?: string;
  filter?: FilterModel;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
}

const ModifiedInPastFilter: FC<ModifiedInPastFilterProps> = ({
  fieldName = CommonFieldName.UPDATED_AT,
  filter,
  onUpdateFilterModel,
}) => {
  const intl = useIntl();

  const days: number | undefined = useMemo(() => {
    const condition = filter?.[fieldName];
    if (!condition || !isSimpleCondition(condition)) {
      return undefined;
    }

    if (condition.operator === FilterOperator.INTERVAL_WITHIN_LAST) {
      const value = -condition.value?.from;
      return DAYS.includes(value) ? value : undefined;
    }

    return undefined;
  }, [fieldName, filter]);

  const handleClick = useCallback(
    (info: MenuInfo) => {
      const value = parseInt(info.key, 10);

      onUpdateFilterModel({
        [fieldName]:
          value === CLEAR_KEY
            ? undefined
            : {
                operator: FilterOperator.INTERVAL_WITHIN_LAST,
                value: getRangeByDuration({ days: value }, true),
              },
      });
    },
    [fieldName, onUpdateFilterModel]
  );

  const menu: MenuProps = useMemo(
    () => ({
      items: [
        ...(days ? [{ key: CLEAR_KEY, label: <b>{intl.formatMessage(messages.clear)}</b> }] : []),
        ...DAYS.map((value) => ({
          key: value,
          label: intl.formatMessage(messages.menuItemLabel, { value }),
        })),
      ],
      onClick: handleClick,
      selectedKeys: days ? [String(days)] : [],
    }),
    [days, handleClick, intl]
  );

  return (
    <Dropdown menu={menu} trigger={["click"]}>
      <RoundButton active={days !== undefined} rightIcon={<FontAwesomeIcon icon={faAngleDown} />}>
        {intl.formatMessage(messages.modifiedInPast, { count: days })}
      </RoundButton>
    </Dropdown>
  );
};

export default ModifiedInPastFilter;
